import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { RelatedNews } from "@/components/RelatedNews";
import { SquadTable } from "@/components/SquadTable";
import { API } from "@/App";
import { Users, Globe, Facebook, Instagram, Twitter } from "lucide-react";

const SOCIALS = [
  ["website", "Official website", Globe, "website"],
  ["facebook_url", "Facebook", Facebook, "facebook"],
  ["instagram_url", "Instagram", Instagram, "instagram"],
  ["x_url", "X", Twitter, "x"],
];
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  TeamHeader, SeasonStats, TopScorer, TeamMatchCard, StandingsPanel, DivisionTeams,
} from "@/components/team/TeamProfileSections";

const PAGE = 10;
const UPCOMING = ["live", "scheduled", "postponed", "suspended"];
const cmp = (a, b) => (a.match_date || "").localeCompare(b.match_date || "") || (a.match_time || "").localeCompare(b.match_time || "");

const SectionTitle = ({ children }) => (
  <h2 className="font-heading text-xl font-black uppercase text-[#09090b] mb-4 border-l-4 border-[#FFC72C] pl-3">{children}</h2>
);

const MatchList = ({ matches, empty, testId, ...rest }) => {
  const [shown, setShown] = useState(PAGE);
  if (!matches.length) return <p className="text-gray-500 py-8 text-center bg-white border border-gray-200" data-testid={`${testId}-empty`}>{empty}</p>;
  return (
    <div className="space-y-2" data-testid={testId}>
      {matches.slice(0, shown).map(m => <TeamMatchCard key={m.match_id} match={m} {...rest} />)}
      {shown < matches.length && (
        <Button variant="outline" className="w-full rounded-none" onClick={() => setShown(shown + PAGE)} data-testid={`${testId}-more`}>
          Show more ({matches.length - shown} remaining)
        </Button>
      )}
    </div>
  );
};

const TeamDetailPage = () => {
  const { teamId } = useParams();
  const [profile, setProfile] = useState(null);
  const [players, setPlayers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [teamsMap, setTeamsMap] = useState({});
  const [compsMap, setCompsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      axios.get(`${API}/teams/${teamId}/profile`),
      axios.get(`${API}/players?team_id=${teamId}&primary_only=true`),
      axios.get(`${API}/matches?team_id=${teamId}`),
      axios.get(`${API}/teams`),
      axios.get(`${API}/competitions`),
    ]).then(([pr, pl, m, t, c]) => {
      setProfile(pr.data);
      setPlayers(pl.data);
      setMatches(m.data);
      setTeamsMap(Object.fromEntries(t.data.map(x => [x.team_id, x])));
      setCompsMap(Object.fromEntries(c.data.map(x => [x.competition_id, x])));
    }).catch(e => setError(e.response?.status === 404 ? "notfound" : "error"))
      .finally(() => setLoading(false));
  }, [teamId]);

  const results = useMemo(() => matches.filter(m => m.status === "completed").sort((a, b) => -cmp(a, b)), [matches]);
  const fixtures = useMemo(() => matches.filter(m => UPCOMING.includes(m.status))
    .sort((a, b) => (a.status === "live" ? -1 : 0) - (b.status === "live" ? -1 : 0) || cmp(a, b)), [matches]);

  if (loading) {
    return (
      <div className="min-h-screen" data-testid="team-detail-loading">
        <Skeleton className="h-[380px] w-full rounded-none" />
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4"><Skeleton className="h-24" /><Skeleton className="h-40" /><Skeleton className="h-64" /></div>
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="team-detail-error">
        <div className="text-center">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-bold text-gray-400">{error === "notfound" ? "Team Not Found" : "Could not load this team"}</h2>
          <Link to="/teams" className="text-[#009E60] hover:underline mt-4 inline-block">Back to Clubs</Link>
        </div>
      </div>
    );
  }

  const { team, primary_competition: primary, other_competitions: others, standings, team_standing, division_teams, top_scorer_leaders } = profile;
  const cardProps = { teamId, teamsMap, compsMap };

  return (
    <div className="bg-gray-50" data-testid="team-detail-page">
      <TeamHeader team={team} primary={primary} others={others} standing={team_standing} />

      <section className="py-10 px-4 md:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-10 min-w-0">
            {(team.description || SOCIALS.some(([k]) => team[k])) && (
              <div data-testid="team-overview">
                <SectionTitle>Club Overview</SectionTitle>
                {team.description && <p className="text-gray-700 leading-relaxed whitespace-pre-line">{team.description}</p>}
                <div className="flex flex-wrap gap-3 mt-3">
                  {SOCIALS.filter(([k]) => team[k]).map(([k, label, Icon, id]) => (
                    <a key={k} href={team[k]} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 text-sm font-semibold text-[#09090b] hover:border-[#009E60] hover:text-[#009E60] transition-colors" data-testid={`team-${id}-link`}>
                      <Icon className="w-4 h-4" /> {label}
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div>
              <SectionTitle>Season Statistics {primary?.season && <span className="text-sm text-gray-400">{primary.season}</span>}</SectionTitle>
              <SeasonStats standing={team_standing} />
            </div>

            <TopScorer leaders={top_scorer_leaders} teamName={team.name} competition={primary} />

            <Tabs defaultValue={results.length ? "results" : "fixtures"} className="w-full">
              <TabsList className="w-full justify-start border-b border-gray-200 mb-6 bg-transparent h-auto p-0 rounded-none">
                {[["results", `Results (${results.length})`], ["fixtures", `Fixtures (${fixtures.length})`], ["squad", `Squad (${players.length})`]].map(([v, label]) => (
                  <TabsTrigger key={v} value={v} className="px-5 py-3 font-heading font-bold uppercase text-gray-500 data-[state=active]:text-[#09090b] data-[state=active]:border-b-4 data-[state=active]:border-[#FFC72C] rounded-none bg-transparent data-[state=active]:shadow-none" data-testid={`tab-${v}`}>
                    {label}
                  </TabsTrigger>
                ))}
              </TabsList>
              <TabsContent value="results">
                <MatchList matches={results} empty="No results yet this season." testId="team-results" {...cardProps} />
              </TabsContent>
              <TabsContent value="fixtures">
                <MatchList matches={fixtures} empty="No upcoming fixtures." testId="team-fixtures" {...cardProps} />
              </TabsContent>
              <TabsContent value="squad">
                {players.length > 0 ? (
                  <div className="bg-white border border-gray-200" data-testid="team-squad-list">
                    <p className="px-4 py-3 text-xs font-bold uppercase tracking-widest text-gray-500 border-b border-gray-200" data-testid="team-squad-count">{players.length} players in squad</p>
                    <SquadTable players={players} testId="team-squad-table" />
                  </div>
                ) : (
                  <p className="text-gray-500 py-8 text-center bg-white border border-gray-200" data-testid="team-squad-empty">No players registered for this team.</p>
                )}
              </TabsContent>
            </Tabs>
          </div>

          <aside className="lg:sticky lg:top-24 self-start min-w-0">
            <StandingsPanel rows={standings} teamId={teamId} competition={primary} />
          </aside>
        </div>
      </section>

      <RelatedNews entity="team_id" id={teamId} title="Club News" />
      <DivisionTeams teams={division_teams} competition={primary} />
    </div>
  );
};

export default TeamDetailPage;
